/* global Config, window */
import { get } from 'lodash';
import { encrypt, mean } from './StatspostUtils';
import { getJson, post } from '../../../utilities/fetchUtils';
import { Stopwatch } from '../../../utilities/timeUtils';
import PlayerManager from '../PlayerManager';

class StatspostManager extends PlayerManager {
  get enabled() {
    return true;
  }

  get events() {
    return {
      onSourceLoaded: () => {
        this.initialize();
      },
      onPlay: () => {
        this._collections.viewedTime.start();
      },
      onPaused: () => {
        this._collections.viewedTime.stop();
      },
      onPlaybackFinished: async () => {
        window.clearInterval(this._collectInterval);
        window.clearInterval(this._sendUpdateReportInterval);
        if (this._collections?.viewedTime?.stop) {
          this._collections.viewedTime.stop();
        }
        this.postReport(await this.createContentEndedReport());
      },
    };
  }

  async initialize() {
    let s = this;
    this._sessionId = Math.floor(Math.random() * Math.pow(2, 31));
    this._sequenceId = 0;
    this._collections = {
      viewedTime: new Stopwatch(),
      bitrates: [],
      droppedFrames: 0,
      stalledTime: 0,
    };

    this._collectInterval = window.setInterval(() => {
      s._collections.bitrates.push(s._delegate.videoData.bitrate);
    }, 10000);

    this._sendUpdateReportInterval = window.setInterval(async () => {
      s.postReport(await s.createContentUpdatedReport());
      this._lastUpdateTs = new Date();
      s._collections.bitrates = [];
      s._collections.droppedFrames = s._delegate.droppedFramesCount;
      s._collections.stalledTime = s._delegate.totalStalledTime;
    }, 15 * 60 * 1000);

    this._lastUpdateTs = new Date();
    this.postReport(await this.createContentStartedReport());
  }

  createReportRecord(reportNumber, currentTime) {
    return {
      timestamp: Math.floor(currentTime / 1000),
      sequenceId: reportNumber,
      '.tagId': 1,
    };
  }

  createIdentityRecord() {
    return {
      subscriberId: App.session.getUuid(),
      serviceId: `${get(this._airing, 'qvt.playback_info.publisher_id', '-1')}|${
        Config.base.env
      }|dany`,
      sessionId: this._sessionId,
      '.tagId': 2,
    };
  }

  createHardwareRecord() {
    return {
      make: 'browser',
      model: 'browser',
      '.tagId': 3,
    };
  }

  createSoftwareRecord() {
    return {
      osName: App.Utilities.getSystemOs(),
      osVersionMajor: this.parseVersion(App.Utilities.getSystemOSVersion()).major,
      osVersionMinor: this.parseVersion(App.Utilities.getSystemOSVersion()).minor,
      osFamily:
        App.Utilities.getSystemOs() === 'win' ? 1 : App.Utilities.getSystemOs() === 'mac' ? 2 : 0,
      platformType: 0,
      playerVersionMajor: this.parseVersion(this._delegate.playerVersion).major,
      playerVersionMinor: this.parseVersion(this._delegate.playerVersion).minor,
      playerVersionRevision: this.parseVersion(this._delegate.playerVersion).revision,
      playerVersionBuild: this.parseVersion(this._delegate.playerVersion).build,
      platformVendor: 'DishAnywhere',
      platformName: App.Utilities.identifyBrowser().split(' ')[0],
      platformVersionMajor: this.parseVersion(App.Utilities.identifyBrowser().split(' ')[1]).major,
      platformVersionMinor: this.parseVersion(App.Utilities.identifyBrowser().split(' ')[1]).minor,
      platformVersionRevision: this.parseVersion(App.Utilities.identifyBrowser().split(' ')[1])
        .revision,
      platformVersionBuild: this.parseVersion(App.Utilities.identifyBrowser().split(' ')[1]).build,
      '.tagId': 4,
    };
  }

  createContentRecord() {
    return {
      assetGuid: get(this._airing, 'qvt.playback_info.asset.guid', null),
      assetPublisherId: get(this._airing, 'qvt.playback_info.publisher_id', '-1'),
      playoutType: 'live',
      '.tagId': 22,
    };
  }

  async createCnpRecord() {
    const ipify = await getJson('https://api.ipify.org?format=json');
    const ip = ipify && ipify.ip;
    const hostnameMatches = this._delegate.streamUrl.match(/^https?\:\/\/([^\/?#]+)(?:[\/?#]|$)/i);
    const hostname = hostnameMatches && hostnameMatches[1];

    return {
      playoutType: 'live',
      sourceIpAddress: ip,
      sourceName: hostname,
      '.tagId': 24,
    };
  }

  createUserExperienceRecord() {
    return {
      bitrateMean: mean(this._collections.bitrates),
      frameDropCnt: this._delegate.droppedFramesCount - this._collections.droppedFrames,
      stallDurationTotalMs: this._delegate.totalStalledTime - this._collections.stalledTime,
      '.tagId': 13,
    };
  }

  createContentStartedRecord() {
    return {
      playoutType: 'live',
      assetGuid: get(this._airing, 'qvt.playback_info.asset.guid', null),
      assetPublisherId: get(this._airing, 'qvt.playback_info.publisher_id', '-1'),
      bestAudioBitrate: parseInt(this._delegate.bestBitRates.audio / 1024),
      bestVideoBitrate: parseInt(this._delegate.bestBitRates.video / 1024),
      '.tagId': 5,
    };
  }

  createContentUpdateRecord() {
    return {
      realTime: new Date() - (this._lastUpdateTs || 0),
      playoutType: 'live',
      viewedTime: this._collections.viewedTime.value,
      assetGuid: get(this._airing, 'qvt.playback_info.asset.guid', null),
      assetPublisherId: get(this._airing, 'qvt.playback_info.publisher_id', '-1'),
      '.tagId': 6,
    };
  }

  createContentEndedRecord() {
    return {
      realTime: new Date() - (this._lastUpdateTs || 0),
      playoutType: 'live',
      assetGuid: get(this._airing, 'qvt.playback_info.asset.guid', null),
      assetPublisherId: get(this._airing, 'qvt.playback_info.publisher_id', '-1'),
      '.tagId': 7,
    };
  }

  createCnpPerformanceRecord() {
    return {
      bitrateMean: mean(this._collections.bitrates),
      '.tagId': 25,
    };
  }

  createProfileRecord() {
    return {
      viewedTime: this._collections.viewedTime.value,
      '.tagId': 19,
    };
  }

  createReport(items) {
    return {
      major: 5,
      minor: 0,
      items,
    };
  }

  postReport(report) {
    post(Config.slingAnalytics.statspostUrl, `JSON1,${encrypt(report)}`, {
      'Content-Type': 'text/plain; charset=UTF-8',
    });
  }

  async createContentStartedReport() {
    return this.createReport([
      this.createReportRecord(this._sequenceId, Date.now()),
      this.createIdentityRecord(),
      this.createHardwareRecord(),
      this.createSoftwareRecord(),
      this.createContentStartedRecord(),
      this.createContentRecord(),
      await this.createCnpRecord(),
    ]);
  }

  async createContentUpdatedReport() {
    return this.createReport([
      this.createReportRecord(this._sequenceId, Date.now()),
      this.createIdentityRecord(),
      this.createHardwareRecord(),
      this.createSoftwareRecord(),
      this.createContentRecord(),
      await this.createCnpRecord(),
      this.createUserExperienceRecord(),
      this.createContentUpdateRecord(),
      this.createProfileRecord(),
      this.createCnpPerformanceRecord(),
    ]);
  }

  async createContentEndedReport() {
    return this.createReport([
      this.createReportRecord(this._sequenceId, Date.now()),
      this.createIdentityRecord(),
      this.createHardwareRecord(),
      this.createSoftwareRecord(),
      this.createContentRecord(),
      await this.createCnpRecord(),
      this.createUserExperienceRecord(),
      this.createContentEndedRecord(),
      this.createProfileRecord(),
      this.createCnpPerformanceRecord(),
    ]);
  }

  parseVersion(versionString) {
    // Assumes a format of: major[.minor[.revision[.build]]]
    // Any field omitted will be zero
    const pattern = /(\d+)(\.\d+)?(\.\d+)?(\.\d+)?/;
    const match = pattern.exec(versionString);
    let target = {
      major: 1,
      minor: 0,
      revision: 0,
      build: 0,
    };
    if (match) {
      if (match[1] !== undefined) {
        target.major = parseInt(match[1]);
      }
      if (match[2] !== undefined) {
        // Trim leading "."
        target.minor = parseInt(match[2].substring(1));
      }
      if (match[3] !== undefined) {
        // Trim leading "."
        target.revision = parseInt(match[3].substring(1));
      }
      if (match[4] !== undefined) {
        // Trim leading "."
        target.build = parseInt(match[4].substring(1));
      }
    }
    return target;
  }
}

export default StatspostManager;
